// PATH: src/pages/CertificatePrintPage.tsx — vue imprimable des documents officiels
import React, { useEffect, useState } from "react";
import { Button, Card, Result, Space } from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useSchoolApi } from "../api/school";
import type { CertificateData } from "../models/school";
import { COLORS } from "../theme";

const titles: Record<CertificateData["doc_type"], string> = {
  CERTIFICAT_SCOLARITE: "CERTIFICAT DE SCOLARITÉ",
  ATTESTATION_INSCRIPTION: "ATTESTATION D'INSCRIPTION",
  // « Reçu » et non « facture » : le document atteste d'un encaissement. Une
  // facture est une pièce fiscale (numérotation ininterrompue, régime de TVA)
  // que le produit ne sait pas encore produire.
  RECU_PAIEMENT: "REÇU DE PAIEMENT",
};

const formatAmount = (v: number) => `${new Intl.NumberFormat("fr-MA").format(v)} DH`;

const CertificatePrintPage: React.FC = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const api = useSchoolApi();

  const docType = (params.get("doc_type") as CertificateData["doc_type"]) || "CERTIFICAT_SCOLARITE";
  // period_id : porté par l'URL pour le reçu, ignoré par les autres documents.
  const rawPeriod = params.get("period_id");
  const periodId = rawPeriod ? Number(rawPeriod) : undefined;
  const [data, setData] = useState<CertificateData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getCertificate(Number(id), docType, periodId)
      .then(setData)
      .catch((err) => setError(err?.response?.data?.error || "Document non délivrable"));
  }, [api, id, docType, periodId]);

  if (error) {
    return <Result status="warning" title={error} extra={<Button onClick={() => navigate(-1)}>Retour</Button>} />;
  }
  if (!data) {
    return <Card loading />;
  }

  const s = data.student;
  const fullName = `${s.last_name} ${s.first_name}`;
  const verb = data.doc_type === "CERTIFICAT_SCOLARITE" ? "est scolarisé(e)" : "est inscrit(e)";
  const isReceipt = data.doc_type === "RECU_PAIEMENT";

  return (
    <div>
      <Space style={{ marginBottom: 16 }} className="no-print">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Retour</Button>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>Imprimer</Button>
      </Space>

      <div
        style={{
          background: "white",
          // Encre fixe : le papier est toujours blanc, le texte ne doit pas suivre le thème sombre.
          color: COLORS.foreground,
          maxWidth: 800,
          margin: "0 auto",
          padding: "60px 70px",
          border: "1px solid #ddd",
          fontFamily: "Georgia, serif",
          lineHeight: 1.9,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          {/* Logo de la fiche École. `crossOrigin` absent volontairement : c'est
              une simple image publique servie par tc_upload. Une URL cassée ne
              doit pas trouer l'en-tête, on masque l'élément en cas d'échec. */}
          {data.logo_url && (
            <img
              src={data.logo_url}
              alt=""
              style={{ maxHeight: 90, maxWidth: 220, objectFit: "contain", marginBottom: 12 }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
          )}
          <h2 style={{ marginBottom: 0 }}>{data.school_name || "Établissement scolaire"}</h2>
          <div style={{ color: "#666" }}>Réf : {data.reference_no}</div>
        </div>

        <h1 style={{ textAlign: "center", fontSize: 22, textDecoration: "underline", marginBottom: 40 }}>
          {titles[data.doc_type]}
        </h1>

        <p>
          Le directeur / La directrice de l'établissement{" "}
          <strong>{data.school_name || "________________"}</strong> atteste que l'élève :
        </p>
        <p style={{ marginLeft: 32 }}>
          <strong>{fullName}</strong>
          {s.last_name_ar && s.first_name_ar ? <span dir="rtl"> ({s.last_name_ar} {s.first_name_ar})</span> : null}
          <br />
          {s.birth_date && (
            <>Né(e) le <strong>{dayjs(s.birth_date).format("DD/MM/YYYY")}</strong>{s.birth_place ? <> à <strong>{s.birth_place}</strong></> : null}<br /></>
          )}
          {s.massar_code && (<>Code Massar : <strong>{s.massar_code}</strong><br /></>)}
        </p>
        {isReceipt ? (
          <>
            <p>
              a réglé les frais de scolarité de la période{" "}
              <strong>{data.period_label}</strong> au titre de l'année scolaire{" "}
              <strong>{data.year_label}</strong>
              {data.class_label && (<>, classe <strong>{data.class_label}</strong></>)}.
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", margin: "28px 0" }}>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #999", padding: "10px 14px" }}>
                    Frais de scolarité — {data.period_label}
                  </td>
                  <td style={{ border: "1px solid #999", padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {/* Un tarif absent au moment du pointage laisse amount_due
                        nul : on le dit, plutôt que d'imprimer « 0 DH » — un
                        montant faux sur un reçu est pire qu'un montant manquant. */}
                    {data.amount != null
                      ? <strong>{formatAmount(data.amount)}</strong>
                      : <em>montant non renseigné</em>}
                  </td>
                </tr>
              </tbody>
            </table>
            {data.paid_at && (
              <p>Règlement enregistré le <strong>{dayjs(data.paid_at).format("DD/MM/YYYY")}</strong>.</p>
            )}
            <p>Le présent reçu est délivré pour servir et valoir ce que de droit.</p>
          </>
        ) : (
          <>
            <p>
              {verb} au titre de l'année scolaire <strong>{data.year_label}</strong>
              {data.level_name && (<> en <strong>{data.level_name}</strong></>)}
              {data.class_label && (<>, classe <strong>{data.class_label}</strong></>)}.
            </p>
            <p>Le présent document est délivré pour servir et valoir ce que de droit.</p>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 60 }}>
          <div>Fait le {dayjs(data.issued_at).format("DD/MM/YYYY")}</div>
          <div style={{ textAlign: "center" }}>
            Signature et cachet
            <div style={{ height: 80 }} />
          </div>
        </div>
      </div>

      <style>{`@media print { .no-print, aside, header { display: none !important; } body { background: white; } }`}</style>
    </div>
  );
};

export default CertificatePrintPage;
